import { Action as AccessTokenAction, ActionType as AccessTokenActionType } from '~/AccessToken';
import { DataSource } from '~/Api/DataSource';
import { ReferencedProductType } from '~/Api/HrPriceReferencing/MaxPrice';
import { HrMaxPrice, Update as ApiUpdate, ValidationResult } from '~/Api/User/ProductGroup/HrMaxPrice';
import { ComparativePriceProduct } from '~/Api/User/ProductGroup/HrMaxPrice/ComparativePriceProduct';

import { Action, ActionType } from './Action';

export interface HrMaxPriceState {
	hrMaxPrice: HrMaxPrice | null;
	isFetching: boolean;
	request: Promise<any>;
}

type UpdateState =
	| {
			name: 'scheduled';
			timeoutId: NodeJS.Timeout;
			data: ApiUpdate;
	  }
	| { name: 'fetching'; request: Promise<any> | null }
	| { name: 'updated' }
	| { name: 'failed' };

export interface Update {
	groupId: string;
	state: UpdateState;
}

export interface Export {
	groupId: string;
	isFetching: boolean;
	request: Promise<any>;
	validationResult: ValidationResult | null;
}

export interface State {
	byId: { [key in string]: HrMaxPriceState };
	export: Export | null;
	update: Update | null;
	productTypes: {
		isFetching: boolean;
		request: Promise<any>;
		types: ReferencedProductType[] | null;
	} | null;
}

export const createState = (): State => ({
	byId: {},
	export: null,
	update: null,
	productTypes: null,
});

// tslint:disable-next-line: cyclomatic-complexity
export const reducer = (state: State = createState(), action: Action | AccessTokenAction): State => {
	switch (action.type) {
		case ActionType.request:
			return {
				...state,
				byId: {
					...state.byId,
					[action.groupId]: {
						hrMaxPrice: state.byId[action.groupId]?.hrMaxPrice ?? null,
						isFetching: true,
						request: action.request,
					},
				},
			};

		case ActionType.receive: {
			const hrMaxPrice = state.byId[action.groupId];
			if (hrMaxPrice?.request !== action.request) {
				return state;
			}

			if (action.hrMaxPrice === null) {
				const byId = { ...state.byId };
				delete byId[action.groupId];
				return { ...state, byId };
			}

			return {
				...state,
				byId: {
					[action.groupId]: {
						...hrMaxPrice,
						hrMaxPrice: action.hrMaxPrice,
						isFetching: false,
					},
				},
			};
		}

		case ActionType.prune: {
			const byId = { ...state.byId };
			delete byId[action.groupId];
			return { ...state, byId };
		}

		case ActionType.scheduleUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'scheduled') {
				clearTimeout(updateState.timeoutId);
			}

			return {
				...state,
				update: {
					groupId: action.groupId,
					state: {
						data: action.update,
						name: 'scheduled',
						timeoutId: action.timeoutId,
					},
				},
			};
		}

		case ActionType.requestUpdate: {
			return {
				...state,
				update: {
					groupId: action.groupId,
					state: { name: 'fetching', request: action.request },
				},
			};
		}

		case ActionType.receiveUpdate: {
			const updateState = state.update?.state;
			if (updateState?.name === 'fetching' && updateState.request !== action.request) {
				return state;
			}

			const byId = { ...state.byId };
			if (action.hrMaxPrice !== null && action.groupId in byId) {
				byId[action.groupId] = {
					...byId[action.groupId],
					hrMaxPrice: action.hrMaxPrice,
				};
			}

			return {
				...state,
				byId,
				export: null,
				update: {
					groupId: action.groupId,
					state: action.hrMaxPrice !== null ? { name: 'updated' } : { name: 'failed' },
				},
			};
		}

		case ActionType.requestXlsCreate:
			return {
				...state,
				export: {
					groupId: action.groupId,
					isFetching: true,
					request: action.request,
					validationResult: null,
				},
			};

		case ActionType.receiveXlsCreate:
			if (state.export?.request !== action.request) {
				return state;
			}

			return {
				...state,
				export: {
					...state.export,
					isFetching: false,
					validationResult: action.validationResult,
				},
			};

		case ActionType.requestProductTypes:
			return {
				...state,
				productTypes: {
					isFetching: true,
					request: action.request,
					types: null,
				},
			};

		case ActionType.receiveProductTypes:
			if (state.productTypes?.request !== action.request) {
				return state;
			}

			return {
				...state,
				productTypes:
					action.types !== null
						? {
								...state.productTypes,
								isFetching: true,
								request: action.request,
								types: action.types,
						  }
						: null,
			};

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
};

export const selectHrMaxPrice = (state: State, groupId: string): HrMaxPriceState | null => state.byId[groupId];

export const selectExport = (state: State, groupId: string): Export | null =>
	state.export?.groupId === groupId ? state.export : null;

export const selectComparativeDataSources = (
	hrMaxPrice: HrMaxPrice
): { dataSources: DataSource[]; products: ComparativePriceProduct[] }[] => {
	if (hrMaxPrice.finalCalculation.comparativePrice === null) {
		return [];
	}
	const rows = hrMaxPrice.finalCalculation.comparativePrice.fromProducts.reduce(
		(
			dataSources: {
				[countryCode: string]: {
					dataSources: { [type: string]: DataSource };
					products: ComparativePriceProduct[];
				};
			},
			product
		) => {
			const countryCode = product.dataSource.country.alpha2;
			if (countryCode in dataSources) {
				dataSources[countryCode].dataSources[product.dataSource.type] = product.dataSource;
				dataSources[countryCode].products.push(product);
			} else {
				dataSources[countryCode] = {
					dataSources: { [product.dataSource.type]: product.dataSource },
					products: [product],
				};
			}
			return dataSources;
		},
		{}
	);

	return Object.values(rows).map((row) => ({
		...row,
		dataSources: Object.values(row.dataSources),
	}));
};
